import BaseLayer from './BaseLayer';

export const WEB_MERCATOR_HALF_WORLD = 20037508.342789244;
export const MAX_MERCATOR_LAT = 85.05112878;

function boundValue(bounds, methodName, propertyName) {
    if (bounds && typeof bounds[methodName] === 'function') {
        return Number(bounds[methodName]());
    }
    return Number(bounds && bounds[propertyName]);
}

export function longitudeToWebMercatorX(longitude) {
    return longitude * WEB_MERCATOR_HALF_WORLD / 180;
}

export function latitudeToWebMercatorY(latitude) {
    const clamped = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, latitude));
    const radians = clamped * Math.PI / 180;
    return WEB_MERCATOR_HALF_WORLD * Math.log(Math.tan(Math.PI / 4 + radians / 2)) / Math.PI;
}

export function lonLatBoundsToWebMercatorBbox(bounds) {
    const west = boundValue(bounds, 'getWest', 'west');
    const south = boundValue(bounds, 'getSouth', 'south');
    const east = boundValue(bounds, 'getEast', 'east');
    const north = boundValue(bounds, 'getNorth', 'north');

    return [
        longitudeToWebMercatorX(west),
        latitudeToWebMercatorY(south),
        longitudeToWebMercatorX(east),
        latitudeToWebMercatorY(north),
    ];
}

// Map the north-up WMS rectangle onto its four projected map corners.
// A projective CSS transform preserves Mercator placement at any bearing/pitch
// without reading the cross-origin image into a canvas.
export function buildViewportImageTransform(map, bounds, width, height) {
    const west = boundValue(bounds, 'getWest', 'west');
    const east = boundValue(bounds, 'getEast', 'east');
    const north = boundValue(bounds, 'getNorth', 'north');
    const south = boundValue(bounds, 'getSouth', 'south');
    const points = [[west, north], [east, north], [east, south], [west, south]]
        .map(point => map.project(point));
    if (!points.every(point => Number.isFinite(point.x) && Number.isFinite(point.y))) return null;
    const [p0, p1, p2, p3] = points;
    const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x;
    const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y;
    const dx3 = p0.x - p1.x + p2.x - p3.x;
    const dy3 = p0.y - p1.y + p2.y - p3.y;
    const determinant = dx1 * dy2 - dx2 * dy1;
    if (Math.abs(determinant) < 1e-12) return null;
    const g = (dx3 * dy2 - dx2 * dy3) / determinant;
    const h = (dx1 * dy3 - dx3 * dy1) / determinant;
    const a = p1.x - p0.x + g * p1.x;
    const b = p3.x - p0.x + h * p3.x;
    const d = p1.y - p0.y + g * p1.y;
    const e = p3.y - p0.y + h * p3.y;
    return 'matrix3d(' + [
        a / width, d / width, 0, g / width,
        b / height, e / height, 0, h / height,
        0, 0, 1, 0,
        p0.x, p0.y, 0, 1,
    ].join(',') + ')';
}

/**
 * Some public WMS services (for example SIDUSIS) do not return
 * Access-Control-Allow-Origin for GetMap, so a MapLibre raster source cannot
 * fetch their images in the browser. The request stays client-side and
 * CORS-free: one ordinary <img> covers the current viewport and follows the
 * map through a CSS transform. No Metroskop proxy or tile cache is used.
 *
 * Subclasses implement buildGetMapUrl() and getAttributionHtml().
 */
export default class ViewportWmsImageLayer extends BaseLayer {
    static IMAGE_DATA_ATTRIBUTE = 'data-wms-image';
    static IMAGE_CLASS_MODIFIER = '';

    constructor(config = {}) {
        super(config);
        this.overlayEl = null;
        this.boundMap = null;
        this.requestSerial = 0;
        this.lastUrl = '';
        this.pendingImage = null;
        this.requestUrl = '';
        this.requestTimer = null;
        this.retryTimer = null;
        this.retryCount = 0;
        this.attributionVisible = false;
        this.handleMoveStart = this.handleMoveStart.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleMoveEnd = this.handleMoveEnd.bind(this);
    }

    buildGetMapUrl() {
        throw new Error(this.id + ' must implement buildGetMapUrl()');
    }

    getAttributionHtml() {
        return '';
    }

    bindMapEvents(map) {
        if (!map || this.boundMap === map) return;
        if (this.boundMap && typeof this.boundMap.off === 'function') {
            this.boundMap.off('movestart', this.handleMoveStart);
            this.boundMap.off('resize', this.handleResize);
            this.boundMap.off('moveend', this.handleMoveEnd);
        }
        this.boundMap = map;
        if (typeof map.on === 'function') {
            map.on('movestart', this.handleMoveStart);
            map.on('resize', this.handleResize);
            map.on('moveend', this.handleMoveEnd);
        }
    }

    handleMoveStart() {
        this.cancelRequest();
        if (this.overlayEl) {
            this.overlayEl.style.visibility = 'hidden';
        }
    }

    handleMoveEnd() {
        this.syncVisibility();
    }

    handleResize() {
        if (this.isActive() && this.isDesired()) {
            this.load();
        }
    }

    attachOverlay(map, image) {
        const host = typeof map.getCanvasContainer === 'function'
            ? map.getCanvasContainer()
            : null;
        const canvas = typeof map.getCanvas === 'function' ? map.getCanvas() : null;
        if (!host) return false;

        if (canvas && canvas.parentNode === host && canvas.nextSibling) {
            host.insertBefore(image, canvas.nextSibling);
        } else if (canvas && canvas.parentNode === host && typeof canvas.after === 'function') {
            canvas.after(image);
        } else {
            host.appendChild(image);
        }

        if (this.overlayEl) this.overlayEl.remove();
        this.overlayEl = image;
        if (this.config.setLayer) this.config.setLayer(image);
        return true;
    }

    setAttributionVisible(visible) {
        if (this.attributionVisible === visible) return;
        this.attributionVisible = visible;
        if (this.config.onAttributionChange) {
            this.config.onAttributionChange(visible ? this.getAttributionHtml() : '');
        }
    }

    cancelRequest() {
        this.requestSerial += 1;
        clearTimeout(this.requestTimer);
        clearTimeout(this.retryTimer);
        this.requestTimer = null;
        this.retryTimer = null;
        if (this.pendingImage) {
            this.pendingImage.onload = null;
            this.pendingImage.onerror = null;
            this.pendingImage.removeAttribute('src');
        }
        this.pendingImage = null;
    }

    viewportSize(map) {
        const canvas = map && typeof map.getCanvas === 'function' ? map.getCanvas() : null;
        const rect = canvas && typeof canvas.getBoundingClientRect === 'function'
            ? canvas.getBoundingClientRect()
            : null;
        const width = rect && rect.width ? rect.width : (canvas && canvas.clientWidth ? canvas.clientWidth : 0);
        const height = rect && rect.height ? rect.height : (canvas && canvas.clientHeight ? canvas.clientHeight : 0);
        return {
            width: Math.max(1, Math.min(2048, Math.round(width || 1))),
            height: Math.max(1, Math.min(2048, Math.round(height || 1))),
        };
    }

    load() {
        if (!this.canActivate() || !this.isDesired()) {
            this.clear();
            return;
        }

        const map = this.getMap();
        if (!map || typeof map.getBounds !== 'function') return;
        this.bindMapEvents(map);
        if (typeof map.isMoving === 'function' && map.isMoving()) return;
        if (typeof document === 'undefined') return;

        const size = this.viewportSize(map);
        const bounds = map.getBounds();
        const transform = buildViewportImageTransform(map, bounds, size.width, size.height);
        if (!transform) return;
        const url = this.buildGetMapUrl(bounds, size.width, size.height);
        if (url === this.lastUrl && this.overlayEl && this.overlayEl.naturalWidth > 0) {
            this.overlayEl.style.transform = transform;
            this.overlayEl.style.visibility = 'visible';
            return;
        }
        if (url === this.requestUrl && (this.pendingImage || this.retryTimer)) return;
        this.cancelRequest();
        if (url !== this.requestUrl) this.retryCount = 0;
        this.requestUrl = url;
        if (this.overlayEl) this.overlayEl.style.visibility = 'hidden';

        const image = document.createElement('img');
        image.className = ['ms-map-wms-image', this.constructor.IMAGE_CLASS_MODIFIER].filter(Boolean).join(' ');
        image.setAttribute(this.constructor.IMAGE_DATA_ATTRIBUTE, '');
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        image.style.width = size.width + 'px';
        image.style.height = size.height + 'px';
        image.style.transform = transform;
        this.pendingImage = image;
        const serial = this.requestSerial;
        image.onload = () => {
            if (serial !== this.requestSerial || !this.isActive() || !this.isDesired()) return;
            if (typeof map.isMoving === 'function' && map.isMoving()) return;
            clearTimeout(this.requestTimer);
            this.requestTimer = null;
            this.pendingImage = null;
            image.onload = null;
            image.onerror = null;
            if (!this.attachOverlay(map, image)) return;
            this.lastUrl = url;
            this.retryCount = 0;
            image.style.visibility = 'visible';
            this.setAttributionVisible(true);
            if (this.config.onLoaded) this.config.onLoaded();
        };
        const failed = () => {
            if (serial !== this.requestSerial) return;
            this.cancelRequest();
            this.setAttributionVisible(false);
            // Bounded retries also cover stalled requests. A later view change
            // or explicit refresh can retry again; failed images never enter DOM.
            if (this.retryCount < 2 && this.isActive() && this.isDesired()) {
                const delay = 1000 * (2 ** this.retryCount++);
                this.retryTimer = setTimeout(() => {
                    this.retryTimer = null;
                    this.load();
                }, delay);
            }
        };
        image.onerror = failed;
        this.requestTimer = setTimeout(failed, 15000);
        image.src = url;
    }

    syncVisibility() {
        const wasActive = this.isActive();
        super.syncVisibility();
        if (wasActive && this.isActive() && this.isDesired()) {
            this.load();
        }
    }

    clear() {
        this.cancelRequest();
        this.lastUrl = '';
        this.requestUrl = '';
        this.retryCount = 0;
        if (this.overlayEl) {
            this.overlayEl.onload = null;
            this.overlayEl.onerror = null;
            this.overlayEl.removeAttribute('src');
            if (typeof this.overlayEl.remove === 'function') this.overlayEl.remove();
        }
        this.overlayEl = null;
        if (this.boundMap && typeof this.boundMap.off === 'function') {
            this.boundMap.off('movestart', this.handleMoveStart);
            this.boundMap.off('moveend', this.handleMoveEnd);
            this.boundMap.off('resize', this.handleResize);
        }
        this.boundMap = null;
        this.setAttributionVisible(false);
        if (this.config.setLayer) this.config.setLayer(null);
        if (this.config.onCleared) this.config.onCleared();
    }
}
